/* eslint-disable no-useless-escape */
/* eslint-disable no-shadow */
import { INPUT_TYPES } from 'app-constants/inputTypes';
import { ACTION_TYPES, COMMENT } from 'app-constants/actionTypes';
import { omit } from 'ramda';
import { isNotEmpty } from 'utils/formUtils';
import { BOT_UPDATE } from 'utils/toastDispacher';
import { getBotspecInfo } from '../selectors';
import { updateParsingBotspec, postBotspec } from './chatbots';
import calculateActionOutput from './outputTypes';
import { injecVariablesInputType } from './inputTypes';
import { validateSpec, updateBotspecParsingErrors, updateIntegrationUrl } from './specValidator';
import { processRulesTransitions, createRulesActions, createRulesFunction } from './specParser/rulesParser';
import { fetchGlobalFunctions } from './globalFunctions';
import slugify from './utils';

const VARIABLE_PATTERN = /\{\u00A0((?:\w?)?(?:_)?\w+)\u00A0\}/g;
const USER_VARIABLE_PATTERN = 'user_';
const CHATBOT_VARIABLE_PATTERN = '_';
const GLOBAL_VARIABLE_PATTERN = 'global_';

export const parseActionForTransition = (node) => {
  const nodeProperties = node.data.properties;
  if (nodeProperties && nodeProperties.type) {
    return slugify(`${node.data.title}_action`);
  }
  return '';
};

const buildNextTransitionId = (nodes, connections) => {
  const nextTransitionKey = connections.length > 0 ? connections[0].node : '';
  return nextTransitionKey ? slugify(`${nodes[nextTransitionKey].data.title}${nextTransitionKey}`) : '';
};

const buildImplicitMapResultTransition = (nodeId, actualNode, nodes) => {
  const { data, outputs } = actualNode;
  const { properties } = data;
  const { input, resultVariables, type } = properties;
  const evaluateCondition = (type === ACTION_TYPES.SEND_MESSAGE && input?.type === INPUT_TYPES.RADIO
    && !input?.optionsVariable)
    || (type === ACTION_TYPES.CONDITIONAL_STEP);

  if (isNotEmpty(resultVariables)) {
    if (evaluateCondition) {
      return {
        [slugify(`${data.title}${nodeId}`)]: {
          next: slugify(`map_result_implicit_${data.title}`),
          action: slugify(`${data.title}${nodeId}_action`),
        },
        [slugify(`map_result_implicit_${data.title}`)]: {
          next: slugify(`conditional_step_implicit_${data.title}`),
          action: slugify(`map_result_implicit${nodeId}_action`),
        },
        [slugify(`conditional_step_implicit_${data.title}`)]: {
          next: Object.keys(outputs).reduce((nodeAccum, outputKey) => {
            const { connections } = outputs[outputKey];
            return { ...nodeAccum, [outputKey]: buildNextTransitionId(nodes, connections) };
          }, {}),
          action: slugify(`conditional_step_implicit_${nodeId}_action`),
        },
      };
    }
    if (properties.type !== ACTION_TYPES.MAP_RESULT) {
      const { connections } = outputs[Object.keys(outputs)[0]];
      return {
        [slugify(`${data.title}${nodeId}`)]: {
          next: slugify(`map_result_implicit_${data.title}`),
          action: slugify(`${data.title}${nodeId}_action`),
        },
        [slugify(`map_result_implicit_${data.title}`)]: {
          next: buildNextTransitionId(nodes, connections),
          action: slugify(`map_result_implicit${nodeId}_action`),
        },
      };
    }
  }

  return {};
};

const buildTransitionKey = (type, outputKey) => {
  if (ACTION_TYPES.CALL_FUNCTION === type || ACTION_TYPES.CONDITIONAL_STEP === type) {
    return outputKey;
  }

  return slugify(`${outputKey}`);
};

export const parseNodeTransitions = (nodes) => Object.keys(nodes).reduce((accum, nodeId) => {
  const node = nodes[nodeId];
  const { title, properties } = node.data;

  const implicitMapResultTransition = buildImplicitMapResultTransition(nodeId, node, nodes);
  const conditionsTransitions = processRulesTransitions(nodeId, node, nodes);

  let next = '';

  const filteredOutputs = Object.keys(node.outputs).filter((key) => key !== 'on_error');
  if (filteredOutputs.length > 1) {
    next = filteredOutputs.reduce((nodeAccum, outputKey) => {
      const { connections } = node.outputs[outputKey];
      const outputId = buildTransitionKey(properties.type, outputKey);
      return { ...nodeAccum, [outputId]: buildNextTransitionId(nodes, connections) };
    }, {});
  } else if (filteredOutputs.length === 1) {
    const { connections } = node.outputs[filteredOutputs[0]];
    next = buildNextTransitionId(nodes, connections);
  }

  return {
    ...accum,
    [slugify(`${title}${nodeId}`)]: {
      next,
      action: slugify(`${title}${nodeId}_action`),
    },
    ...implicitMapResultTransition,
    ...conditionsTransitions,
  };
}, {});

const parseGenericInputType = (property) => ({
  ...property,
  data: [
    property.data.placeholder && { placeholder: property.data.placeholder },
  ],
  method: 'keyboard',
});

const parseNumberInputType = (property) => {
  const {
    placeholder,
    integer,
    min,
    max,
  } = property?.data;
  return {
    ...property,
    data: [
      { placeholder },
      { integer },
      { min },
      { max },
    ],
    method: 'keyboard',
  };
};

const putVariableName = (variable) => (variable.includes('.') ? `{{ variables.${variable} | to_json_str }}` : `{{ variables.session.${variable} | to_json_str }}`);

const parseRadioCheckboxInputType = (property) => {
  // If optionsVariable is defined, use it to get the options from a variable
  const data = property.optionsVariable && property.optionsVariable.length > 0
    ? putVariableName(property.optionsVariable)
    : Object.keys(property.data).reduce(
      (accum, key) => [
        ...accum, { id: property.data[key].value, label: property.data[key].name },
      ], [],
    );

  return {
    type: property.type,
    data,
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
};

const parseDateInputType = (property) => {
  const date = new Date(property.data.default);
  return {
    type: 'date',
    data: [
      { format: property.data.format },
      { placeholder: property.data.format },
      {
        default: property.data.useDefaultDate
          ? {} : { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() },
      },
    ],
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
};

const parsePasswordLocationInputType = (property) => ({
  ...property,
  data: Object.keys(property.data).reduce(
    (accum, key) => [
      ...accum, { [key]: property.data[key] },
    ], [],
  ),
  method: 'keyboard',
});

const parseMediaInputType = (property) => ({
  ...property,
  geotag: property.data.geotag,
  data: [
    property.data.media_type && { media_type: property.data.media_type },
    ...property.data.picker ? [{ picker: property.data.picker }] : [],
    ...property.data.camera ? [{ facing: property.data.camera }] : [],
  ],
  method: 'keyboard',
});

const parseBarcodeInputType = (property) => {
  const data = {
    type: 'barcode',
    data: [{ response: property.data.response }],
    geotag: property.data.geotag,
    method: 'keyboard',
    ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
  };
  if (property.data.subtype) {
    data.subtype = property.data.subtype;
  }
  return data;
};

const parseSignatureInputType = (property) => ({
  type: 'signature',
  data: [{ caption: property.data.caption }],
  geotag: property.data.geotag,
  method: 'keyboard',
  ...property.timeout > 0 && property.timeout <= 10 && { timeout: property.timeout },
});

const parseInputType = (property) => {
  const parseInputType = {
    [INPUT_TYPES.TEXT]: parseGenericInputType,
    [INPUT_TYPES.NUMBER]: parseNumberInputType,
    [INPUT_TYPES.PHONE]: parseGenericInputType,
    [INPUT_TYPES.EMAIL]: parseGenericInputType,
    [INPUT_TYPES.RADIO]: parseRadioCheckboxInputType,
    [INPUT_TYPES.CHECKBOX]: parseRadioCheckboxInputType,
    [INPUT_TYPES.DATE]: parseDateInputType,
    [INPUT_TYPES.PASSWORD]: parsePasswordLocationInputType,
    [INPUT_TYPES.LOCATION]: parsePasswordLocationInputType,
    [INPUT_TYPES.MEDIA]: parseMediaInputType,
    [INPUT_TYPES.BARCODE]: parseBarcodeInputType,
    [INPUT_TYPES.SIGNATURE]: parseSignatureInputType,
  };

  return parseInputType[property.type](property);
};

const verifyMetadata = (metadata) => isNotEmpty(metadata)
  && Object.values(metadata).every((val) => !!val);

const parseSendMessageAction = (properties) => {
  const parseMessages = (messages) => Object.keys(messages).reduce((accum, key) => {
    const message = messages[key];

    if (message.data && Object.keys(message.data).length) {
      const data = Object.keys(message.data).reduce((accum, key) => {
        const { name, value } = message.data[key];
        return { ...accum, [name]: value };
      }, {});
      return [...accum, { template: message.text, data }];
    }

    return [...accum, message.text];
  }, []);

  return {
    type: 'send_message',
    ...isNotEmpty(properties?.vars) && { vars: properties.vars },
    ...properties.messages && { messages: parseMessages(properties.messages) },
    ...isNotEmpty(properties?.input) && { input: parseInputType(properties.input) },
    ...properties?.closeChat && { close_chat: !properties.closeChat },
    ...properties?.mediaUrl && { media_url: properties.mediaUrl },
    ...properties?.delay > 0 && { delay: properties.delay },
    ...properties?.timeout > 0 && properties?.timeout <= 10 && { timeout: properties.timeout },
    ...isNotEmpty(properties?.metadata) && verifyMetadata(properties?.metadata)
    && { metadata: properties.metadata },
  };
};

const parseCallApiAction = (properties, nodeId, nodes) => {
  const HTTP_OK = 200;
  const DEFAULT_TIMECAP = '0';
  const DEFAULT_SCHEMA = {};

  const params = Object.keys(properties.params).reduce((accum, key) => {
    const values = [...Object.values(properties.params[key])];
    const paramName = values[0];
    const paramValue = values[1];

    if (accum[paramName]) {
      if (Array.isArray(accum[paramName])) {
        return { ...accum, [paramName]: [...accum[paramName], paramValue] };
      }
      return { ...accum, [paramName]: [accum[paramName], paramValue] };
    }

    return { ...accum, [paramName]: paramValue };
  }, {});

  const headers = Object.keys(properties.headers).reduce((accum, key) => {
    const values = [...Object.values(properties.headers[key])];
    return { ...accum, [values[0]]: values[1] };
  }, {});

  const { connections } = nodes[nodeId].outputs.on_error;
  let onErrorNextTransition = '';

  if (connections.length > 0) {
    onErrorNextTransition = buildNextTransitionId(nodes, connections);
  }

  const {
    type, vars, method, url, body, postVerify, contentType,
    integration, integrationService, timeout,
  } = properties;

  const status = postVerify.status ? postVerify.status.map((code) => parseInt(code.split(' ')[0], 10)) : HTTP_OK;

  return {
    type,
    vars,
    eval: {
      method: method.toUpperCase(),
      content_type: contentType,
      url,
      params: JSON.stringify(params),
      headers,
      ...body.length > 2 && { body: JSON.stringify(body) },
      integration,
      integration_service: integrationService,
      timeout: parseInt(timeout || '0', DEFAULT_TIMECAP),
    },
    post_eval: {
      verify: {
        ...contentType === 'application/json' ? { schema: postVerify.schema ? JSON.parse(postVerify.schema) : DEFAULT_SCHEMA } : {},
        status,
        timecap: parseInt(postVerify.timecap || '0', DEFAULT_TIMECAP),
      },
    },
    ...onErrorNextTransition.length > 0 && { on_error: { next: onErrorNextTransition } },
  };
};

const parseImplicitMapResultAction = (nodeId, type, properties) => {
  const { input, title } = properties;
  const transitionId = slugify(`${title}${nodeId}`);
  const reference = calculateActionOutput(type, transitionId, input, properties);
  const nestedReferences = Object.keys(properties.resultVariables)
    .reduce((accum, key) => ({
      ...accum,
      [key]: { reference: properties.resultVariables[key].reference },
    }), {});

  const flattenedReferences = {
    ...reference,
    ...reference.single,
    ...nestedReferences,
  };

  return {
    type: 'map_result',
    eval: {
      targets: Object.keys(properties.resultVariables).reduce((accum, key) => {
        const referenceParts = flattenedReferences[key].reference.split('.');
        const resultMap = referenceParts[referenceParts.length - 1].trim();

        return [...accum, {
          from: `{{ ${flattenedReferences[key].reference.substring(0, flattenedReferences[key].reference.indexOf(`.${resultMap}`))} }}`,
          assign: [{
            map: resultMap,
            to: `variables.session.${properties.resultVariables[key].variableId}`,
          }],
        }];
      }, []),
    },
  };
};

const parseMapResultAction = (properties) => ({
  type: 'map_result',
  vars: properties.vars,
  eval: {
    targets: Object.keys(properties.targets).reduce(
      (accum, key) => [
        ...accum,
        {
          from: `${properties.targets[key].from}`, assign: properties.targets[key].assignments,
        },
      ], [],
    ),
  },
});

const parseCallFuntionAction = (properties, nodeId, nodes) => {
  const { connections } = nodes[nodeId].outputs.on_error;
  let onErrorNextTransition = '';

  if (connections.length > 0) {
    onErrorNextTransition = buildNextTransitionId(nodes, connections);
  }
  return {
    type: 'call_function',
    vars: properties.vars,
    eval: {
      function: properties.eval.function,
      input: properties.eval.input,
    },
    ...onErrorNextTransition.length > 0 && { on_error: { next: onErrorNextTransition } },
  };
};

const parseEvalNextAction = (properties) => ({
  type: 'eval_next',
  vars: properties.vars,
  eval: {
    function: properties.eval.function,
    input: properties.eval.input,
  },
});

const parseConditionalStepAction = (properties) => ({
  type: 'conditional_step',
  vars: properties.vars,
  eval: {
    function: properties.eval.function,
    input: properties.eval.input,
  },
});

const parseImplicitConditionalStepAction = (functionId, transitionId, radioOptions) => ({
  type: 'conditional_step',
  eval: {
    function: functionId,
    input: {
      operation: `{{ ${transitionId} }}`,
    },
    outputs: Object.values(radioOptions).map((option) => option.name),
  },
});

const parseStoreDataAction = (properties) => ({
  type: 'store_data',
  eval: properties.eval,
});

const parseStoreFileAction = (properties) => ({
  type: 'store_data',
  eval: properties.eval,
});

const replacer = (variables, isJinja = true) => (match) => {
  const key = match.split(VARIABLE_PATTERN).filter((key) => !!key)[0];
  if (key.startsWith(USER_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('user_', '');
    return `{{ variables.user.${formattedKey} }}`;
  }
  if (key.startsWith(GLOBAL_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('global_', '');
    return `{{ variables.global.${formattedKey} }}`;
  }
  if (key.startsWith(CHATBOT_VARIABLE_PATTERN)) {
    const formattedKey = key.substring(1);
    return (variables[formattedKey] && `{{ variables.chatbot.${formattedKey} }}`) || key;
  }

  if (!isJinja) {
    return `variables.session.${key}`;
  }

  return `{{ variables.session.${key} }}`;
};

const replacerBody = (variables, isJinja = true) => (match) => {
  const key = match.split(VARIABLE_PATTERN).filter((key) => !!key)[0];
  if (key.startsWith(USER_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('user_', '');
    return `{{ variables.user.${formattedKey} }}`;
  }
  if (key.startsWith(GLOBAL_VARIABLE_PATTERN)) {
    const formattedKey = key.replace('global_', '');
    return `{{ variables.global.${formattedKey} }}`;
  }
  if (key.startsWith(CHATBOT_VARIABLE_PATTERN)) {
    const formattedKey = key.substring(1);
    return (variables[formattedKey] && `{{ variables.chatbot.${formattedKey} }}`) || key;
  }

  if (!isJinja) {
    return `variables.session.${key}`;
  }

  return `{{ variables.session.${key} }}`;
};

const injectKeyValue = (properties, variables) => Object.entries(properties).reduce(
  (accum, [key, val]) => ({
    ...accum,
    [key.replace(VARIABLE_PATTERN, replacer(variables)) || key]: val.replace(
      VARIABLE_PATTERN, replacer(variables),
    ),
  }), {},
);

const injectVariablesOnTargets = (targets, variables) => Object.keys(targets).reduce(
  (accum, targetKey) => {
    const target = targets[targetKey];
    return {
      ...accum,
      [targetKey]: {
        from: target.from.replace(VARIABLE_PATTERN, replacer(variables)),
        assignments: target.assignments.reduce((accum, assignment) => [
          ...accum,
          {
            map: assignment.map.replace(VARIABLE_PATTERN, replacer(variables)),
            to: assignment.to.replace(VARIABLE_PATTERN, replacer(variables, false)),
          },
        ], []),
      },
    };
  }, {},
);

const injectVariablesOnSendMessage = (nodeProperties, variables) => {
  const {
    messages, input, mediaUrl,
    metadata, targets,
  } = nodeProperties;
  const injectMessages = (messages) => Object.keys(messages).reduce((accum, key) => ({
    ...accum,
    [key]: {
      ...messages[key],
      text: messages[key].text.replace(VARIABLE_PATTERN, replacer(variables)),
      data: Object.keys(messages[key].data).reduce(
        (accum, dataKey) => (
          { ...accum, [dataKey]: injectKeyValue(messages[key].data[dataKey], variables) }
        ), {},
      ),
    },
  }), {});

  return {
    ...nodeProperties,
    messages: messages ? injectMessages(messages) : {},
    input: isNotEmpty(input)
      ? injecVariablesInputType(input, VARIABLE_PATTERN, replacer(variables)) : {},
    mediaUrl: mediaUrl?.replace(VARIABLE_PATTERN, replacer(variables)) ?? {},
    metadata: metadata ? injectKeyValue(metadata, variables) : {},
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesOnCallApi = (nodeProperties, variables) => {
  const {
    url, params, headers, body,
    targets,
  } = nodeProperties;

  const injectOnParams = (params) => Object.keys(params).reduce((accum, key) => ({
    ...accum,
    [key]: injectKeyValue(params[key], variables),
  }), {});

  const injectOnHeaders = (headers) => Object.keys(headers).reduce((accum, key) => ({
    ...accum,
    [key]: injectKeyValue(headers[key], variables),
  }), {});

  return {
    ...nodeProperties,
    url: url.replace(VARIABLE_PATTERN, replacer(variables)),
    params: injectOnParams(params),
    headers: injectOnHeaders(headers),
    body: body.replace(VARIABLE_PATTERN, replacerBody(variables)),
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesGeneric = (nodeProperties, variables) => {
  const { eval: functionEval, targets } = nodeProperties;

  return {
    ...nodeProperties,
    eval: {
      ...functionEval,
      input: functionEval.input ? injectKeyValue(functionEval.input, variables) : {},
    },
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesOnMapResult = (nodeProperties, variables) => {
  const { targets } = nodeProperties;

  return {
    ...nodeProperties,
    targets: targets ? injectVariablesOnTargets(targets, variables) : {},
  };
};

const injectVariablesOnStoreData = (nodeProperties) => nodeProperties;

const injectVariablesOnStoreFile = (nodeProperties) => nodeProperties;

const injectVariablesOnAction = (type, nodeProperties, variables) => {
  const injectors = {
    [ACTION_TYPES.SEND_MESSAGE]: injectVariablesOnSendMessage,
    [ACTION_TYPES.CALL_API]: injectVariablesOnCallApi,
    [ACTION_TYPES.MAP_RESULT]: injectVariablesOnMapResult,
    [ACTION_TYPES.CALL_FUNCTION]: injectVariablesGeneric,
    [ACTION_TYPES.EVAL_NEXT]: injectVariablesGeneric,
    [ACTION_TYPES.STORE_DATA]: injectVariablesOnStoreData,
    [ACTION_TYPES.STORE_FILE]: injectVariablesOnStoreFile,
    [ACTION_TYPES.CONDITIONAL_STEP]: injectVariablesGeneric,
  };

  return injectors[slugify(type)](nodeProperties, variables);
};

const resolveImplicitMapResult = (type, properties, nodeId, variables) => {
  if (
    type !== ACTION_TYPES.MAP_RESULT
    && isNotEmpty(properties.resultVariables)
  ) {
    const updatedProperties = injectVariablesOnMapResult(properties, variables);

    return {
      [`map_result_implicit${nodeId}_action`]: {
        ...parseImplicitMapResultAction(nodeId, type, updatedProperties),
      },
    };
  }

  return {};
};

const resolveImplicitConditionalStep = (type, properties, nodeId, title) => {
  const { input, resultVariables } = properties;
  if (
    isNotEmpty(resultVariables)
    && type === ACTION_TYPES.SEND_MESSAGE && input?.type === INPUT_TYPES.RADIO
    && !input?.optionsVariable
  ) {
    const functionId = `implicit_function${nodeId}`;
    const transitionId = `transition.${slugify(title + nodeId)}.output.answer.id`;
    const radioOptions = input.data;

    return {
      [`conditional_step_implicit${nodeId}_action`]: {
        ...parseImplicitConditionalStepAction(functionId, transitionId, radioOptions),
      },
    };
  }

  return {};
};

export const parseNodeActions = (nodes, variables) => {
  const parseActions = {
    [ACTION_TYPES.SEND_MESSAGE]: parseSendMessageAction,
    [ACTION_TYPES.CALL_API]: parseCallApiAction,
    [ACTION_TYPES.MAP_RESULT]: parseMapResultAction,
    [ACTION_TYPES.CALL_FUNCTION]: parseCallFuntionAction,
    [ACTION_TYPES.EVAL_NEXT]: parseEvalNextAction,
    [ACTION_TYPES.STORE_DATA]: parseStoreDataAction,
    [ACTION_TYPES.CONDITIONAL_STEP]: parseConditionalStepAction,
    [ACTION_TYPES.STORE_FILE]: parseStoreFileAction,
  };

  const actions = Object.keys(nodes).reduce((accum, nodeId) => {
    const node = nodes[nodeId];
    const { properties: nodeProperties, title } = node.data;
    const { type } = nodeProperties;
    const implicitMapResult = resolveImplicitMapResult(type, nodeProperties, nodeId, variables);
    const implicitConditionalStep = resolveImplicitConditionalStep(
      type, nodeProperties, nodeId, slugify(title),
    );

    const conditionsActions = createRulesActions(
      type,
      nodeProperties,
      nodeId,
      slugify(title),
      variables,
    );

    const propertiesWithVariables = injectVariablesOnAction(type, nodeProperties, variables);

    let actions = {
      [slugify(`${nodes[nodeId].data.title}${nodeId}_action`)]: {
        ...parseActions[slugify(type)](propertiesWithVariables, nodeId, nodes),
      },
    };

    if (implicitMapResult) {
      actions = {
        ...actions,
        ...implicitMapResult,
        ...implicitConditionalStep,
        ...conditionsActions,
      };
    }
    return {
      ...accum,
      ...actions,
    };
  }, {});

  return actions;
};

const markEntryPoint = (nodes) => {
  const key = Object.keys(nodes).find((key) => nodes[key].data.isEntryPoint);
  const nodeEntryPointId = nodes[key] ? `${slugify(nodes[key].data.title)}${key}` : '';
  return nodeEntryPointId;
};

const createImplicitFunction = (functionId, radioOptions) => ({
  [functionId]: {
    runtime: 'js',
    version: '5',
    code: `function ${functionId}(input) {\n  const operation = input.operation;\n  return operation;\n}`,
    input: {
      operation: 'string',
    },
    output: {
      type: 'string',
      values: radioOptions,
    },
  },
});

const resolveImplicitFunctions = (nodes, functions) => Object.keys(nodes).reduce(
  (accum, nodeId) => {
    const node = nodes[nodeId];
    const { data } = node;
    const { properties } = data;
    const {
      input,
      resultVariables,
      type: actionType,
    } = properties;

    if (
      isNotEmpty(resultVariables)
      && actionType === ACTION_TYPES.SEND_MESSAGE && properties.input?.type === INPUT_TYPES.RADIO
      && !input?.optionsVariable
    ) {
      const { data: radioOptions } = input;
      const ids = Object.values(radioOptions).map((option) => option.name);
      return { ...accum, ...createImplicitFunction(`implicit_function${nodeId}`, ids) };
    }

    return accum;
  }, { ...functions },
);

const filterVariablesMetadata = (variables) => Object.keys(variables).reduce((accum, key) => {
  const { isMapped, ...properties } = variables[key];
  return { ...accum, [key]: properties };
}, {});

const parseNodes = (specJson, reteJson, globalVariables = {}, globalFunctions = {}) => {
  const variables = {
    ...specJson.variables.chatbot,
    ...filterVariablesMetadata(specJson.variables.session),
    ...specJson.variables.user,
    ...globalVariables,
  };

  const globalVariablesProperties = Object.keys(globalVariables).reduce((acc, varKey) => {
    const varValue = globalVariables[varKey];
    return {
      ...acc,
      [varKey]: {
        type: varValue.type,
        value: varValue.value,
      },
    };
  }, {});

  const nodes = Object.keys(reteJson.nodes).reduce((accum, key) => {
    const actualNode = reteJson.nodes[key];
    if (slugify(actualNode.name) !== COMMENT) {
      return { ...accum, [key]: actualNode };
    }
    return accum;
  }, {});

  const transitions = parseNodeTransitions(nodes);
  const actions = parseNodeActions(nodes, variables);
  const entrypoint = markEntryPoint(nodes);
  const functions = {
    ...specJson.functions,
    ...globalFunctions,
    ...resolveImplicitFunctions(nodes),
    ...createRulesFunction(nodes, variables),
  };

  return {
    ...specJson,
    entrypoint,
    transitions,
    actions,
    functions,
    variables: {
      ...specJson.variables,
      session: filterVariablesMetadata(specJson.variables.session),
      global: filterVariablesMetadata(globalVariablesProperties),
    },
  };
};

export const fetchGlobalVariablesInternal = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  return api.variables.list()
    .then((variables) => {
      // Convertir el array de variables a un objeto con el nombre como key
      const variablesObject = variables.reduce((acc, variable) => {
        acc[variable.name] = variable;
        return acc;
      }, {});
      return variablesObject;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching global variables:', error);
      return {};
    });
};

export const parseProdJson = (json) => (dispatch, getState) => {
  dispatch(updateBotspecParsingErrors([]));
  const { botspec } = getBotspecInfo(getState());
  const reteJson = { ...botspec.rete_json, ...json };

  const errorMessages = validateSpec(reteJson);
  if (errorMessages.length !== 0) {
    dispatch(updateBotspecParsingErrors(errorMessages));
  }

  const specJson = omit(['entrypoint', 'transitions', 'actions'], botspec.spec_json);

  dispatch(updateParsingBotspec(true));

  Promise.all([
    dispatch(fetchGlobalVariablesInternal()),
    dispatch(fetchGlobalFunctions()),
  ])
    .then(([globalVariables, globalFunctions]) => {
      const parsedSpecJson = parseNodes(
        specJson,
        reteJson,
        globalVariables || {},
        globalFunctions || {},
      );
      const updatedSpecJson = updateIntegrationUrl(parsedSpecJson);

      dispatch(
        postBotspec({
          ...botspec,
          spec_json: updatedSpecJson,
          rete_json: reteJson,
        }, {
          actionId: BOT_UPDATE,
          elementName: botspec.name,
        }),
      ).then(() => {
        dispatch(updateParsingBotspec(false));
      });
    })
    .catch(() => {
      const parsedSpecJson = parseNodes(specJson, reteJson, {}, {});
      const updatedSpecJson = updateIntegrationUrl(parsedSpecJson);

      dispatch(
        postBotspec({
          ...botspec,
          spec_json: updatedSpecJson,
          rete_json: reteJson,
        }, {
          actionId: BOT_UPDATE,
          elementName: botspec.name,
        }),
      ).then(() => {
        dispatch(updateParsingBotspec(false));
      });
    });
};

export const updateBotspecsVariable = (botspec) => (dispatch) => {
  dispatch(updateParsingBotspec(true));
  Promise.all([
    dispatch(fetchGlobalVariablesInternal()),
    dispatch(fetchGlobalFunctions()),
  ])
    .then(([globalVariables, globalFunctions]) => {
      const parsedSpecJson = parseNodes(
        botspec.spec_json,
        botspec.rete_json,
        globalVariables || {},
        globalFunctions || {},
      );

      dispatch(
        postBotspec({
          ...botspec,
          spec_json: parsedSpecJson,
          rete_json: botspec.rete_json,
        }, {
          actionId: BOT_UPDATE,
          elementName: botspec.name,
        }),
      ).then(() => {
        dispatch(updateParsingBotspec(false));
      });
    })
    .catch(() => {
      dispatch(updateParsingBotspec(false));
    });
};

export default parseNodes;
